import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { Modal } from 'flowbite';
import moment from 'moment';

import { decimalUpTo } from 'Helpers/currencyFormat';
import { modalOptions } from 'Helpers/modalOptions';
import { downloadSource } from 'Helpers/urlService';

import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';

function BudgetsItem(props) {
    const [budget, setBudget] = useState({});
    const [evolvingBudget, setEvolvingBudget] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [disabledSubmit, setDisabledSubmit] = useState(false);
    const [error, setError] = useState(false);
    const [addAttachmentsModalElement, setAddAttachmentsModalElement] = useState(null);

    const addAttachmentsModalRef = useRef();

    useEffect(() => {
        fetchBudget();
        addAttachmentsModalRef && setAddAttachmentsModalElement(new Modal(addAttachmentsModalRef.current, modalOptions(false)));
    }, []);

    const fetchBudget = () => {
        axios
            .get(window.location.href)
            .then((res) => {
                setBudget(res.data.budget);
                setEvolvingBudget(res.data.evolvingBudget);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const getDownloadUrl = (budgetId, attachmentId) => {
        return downloadAttachmentUrl.replace('budget_id', budgetId).replace('attachment_id', attachmentId);
    };

    const getUploadUrl = (budgetId) => {
        return uploadAttachmentUrl.replace('budget_id', budgetId);
    };

    const downloadAttachment = (e, budgetId, attachmentId, name) => {
        e.preventDefault();
        downloadSource(name, getDownloadUrl(budgetId, attachmentId));
    };

    const changeHandler = (event) => {
        setSelectedFiles(event.target.files);
    };

    const handleSubmission = (url) => {
        if (selectedFiles.length && canUpdate) {
            setDisabledSubmit(true);
            const formData = new FormData();
            for (let i = 0; i < selectedFiles.length; i++) {
                formData.append(`files[${i}]`, selectedFiles[i]);
            }

            axios({
                url: url,
                method: 'post',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            })
                .then((response) => {
                    closeModal();
                    fetchBudget();
                })
                .catch((e) => {
                    setError(e?.response?.data);
                    console.log(e);
                })
                .finally(() => {
                    setDisabledSubmit(false);
                    setSelectedFiles([]);
                    $('#budget-attachments').val(null);
                });
        } else {
            closeModal();
        }
    };

    const openModal = () => {
        addAttachmentsModalElement && addAttachmentsModalElement.show();
    };

    const closeModal = () => {
        addAttachmentsModalElement && addAttachmentsModalElement.hide();
    };

    return (
        <div>
            <h4 className="text-blue font-weight-600 mb-0">{$t('Budget Info')}</h4>
            <div className="card">
                <div className="card-body">
                    <div className="row">
                        <div className="col">
                            <h5 className="text-blue font-weight-600 mt-1">{$t('Details')}</h5>
                            <div className="row text-secondary mb-1">
                                <div className="col">{$t('Name')}:</div>
                                <div className="col">
                                    <b>{budget.name}</b>
                                </div>
                            </div>
                            <div className="row text-secondary mb-1">
                                <div className="col">{$t('Version')}:</div>
                                <div className="col">{budget.version}</div>
                            </div>
                            <div className="row text-secondary mb-1">
                                <div className="col">{$t('Date')}:</div>
                                <div className="col">{budget.created_at}</div>
                            </div>
                        </div>
                        <div className="col">
                            <div className="row">
                                <div className="col">
                                    <h5 className="text-blue font-weight-600 mt-1">{$t('Attachments')}:</h5>
                                    {budget.attachments && budget.attachments.length > 0 && (
                                        <ul className="list-group list-group-flush">
                                            {budget.attachments.map((attachment) => {
                                                return (
                                                    <li className="list-group-item" key={attachment.id}>
                                                        <a
                                                            href=""
                                                            onClick={(e) => {
                                                                downloadAttachment(e, budget.id, attachment.id, attachment.name);
                                                            }}
                                                        >
                                                            {attachment.name}
                                                        </a>{' '}
                                                        ({$t('Uploaded')}: {moment(attachment.created_at).format('YYYY-MM-DD')})
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                    {budget.attachments && budget.attachments.length === 0 && <div className="col">{$t('No attachments')}</div>}
                                    {!!canUpdate && !!budget.is_locked && (
                                        <div className="col d-flex justify-content-center">
                                            <Button variant="primary" onClick={() => openModal()}>
                                                {$t('Upload Attachments')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mt-3">
                <div className="card-header">{$t('Totals')}</div>
                <div className="card-body">
                    <table className="table table-bordered">
                        <thead>
                            <tr>
                                <th scope="col">{$t('Budget name')}</th>
                                <th scope="col">{$t('Revenue')}</th>
                                <th scope="col">{$t('Expenditure')}</th>
                                <th scope="col">{$t('Profit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {budget.totals && (
                                <tr>
                                    <th>{budget.name}</th>
                                    <td>{decimalUpTo(budget.totals.revenue)}</td>
                                    <td>{decimalUpTo(budget.totals.expenditure)}</td>
                                    <td>{decimalUpTo(budget.totals.profit)}</td>
                                </tr>
                            )}
                            {evolvingBudget && evolvingBudget.totals && (
                                <tr>
                                    <th>{evolvingBudget.name}</th>
                                    <td>{decimalUpTo(evolvingBudget.totals.revenue)}</td>
                                    <td>{decimalUpTo(evolvingBudget.totals.expenditure)}</td>
                                    <td>{decimalUpTo(evolvingBudget.totals.profit)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/*Modal*/}
            <div id="addAttachments" ref={addAttachmentsModalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
                <div className="modal__container">
                    <div className="modal__inner-container">
                        <ModalHeader title={$t('Choose file')} close={closeModal}></ModalHeader>
                        <div className="tw-rounded-b">
                            {disabledSubmit ? (
                                <LoadingSpinner />
                            ) : (
                                <div className="modal__form-container">
                                    <form id="add-attachment-form" method="post" encType="multipart/form-data">
                                        <div className="tw-grid tw-grid-cols-1">
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label className="text-input__label">{$t('Attachments')}:</label>
                                                <input
                                                    className={error ? 'tw-border tw-border-red-500 file-input' : 'tw-border-0 file-input'}
                                                    id="budget-attachments"
                                                    type="file"
                                                    name="files[]"
                                                    multiple
                                                    placeholder="Select Files"
                                                    accept={ACCEPTED_ATTACHMENT_FILE_TYPES}
                                                    onChange={changeHandler}
                                                />
                                                {error && <div className="tw-text-red-500 tw-text-sm">{$t('Invalid file type or extension')}</div>}
                                            </div>
                                        </div>
                                    </form>
                                    <ModalFooter
                                        cancelButtonText={$t('Close')}
                                        primaryButtonText={$t('Upload')}
                                        onCancel={closeModal}
                                        handleSubmit={() => {
                                            handleSubmission(getUploadUrl(budget.id));
                                        }}
                                        isSubmitting={disabledSubmit}
                                    ></ModalFooter>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BudgetsItem;

if (document.getElementById('budgetsItem')) {
    var rootEl = document.getElementById('budgetsItem'); // data attributes below help us
    ReactDOM.render(
        <App>
            <BudgetsItem />
        </App>,
        rootEl,
    );
}
